import * as React from 'react';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import {
  DataGridPro,
  GridColDef,
  GridToolbarContainer,
  GridColumnMenuProps,
  GridColumnMenu,
  GridLoadingOverlayProps,
  LoadingOverlayPropsOverrides,
  GridRenderCellParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { CircularProgress, LinearProgress } from '@mui/material';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  searchOrders,
  setSearchInput,
  setSearchLoading,
  setSelectedOrders,
} from '../../../../state/orderSearchModalSlice';
import { useDispatch, useSelector } from '../../../../state';
import type { OrderSearchInput } from '@repo/api-contract';
import { useCallback } from 'react';
import { debounce } from '@mui/material/utils';
import SearchByDropdown from './SearchByDropdown';
import { useTranslation } from 'react-i18next';
import hexToRGBA from '../../../../util/converters/hexToRGBA';
import { initialPageSize } from './constants';
import {
  EMPTY_LEADING_GRID_COLUMNS,
  GRID_CHECKBOX_COLUMN_ORDER,
  usePersistedDataGridColumnPreferences,
} from '../../../../util/hooks';
import type { DataGridTableId } from '../../../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../../../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'search-orders' satisfies DataGridTableId;
const KEY_GLOBAL_COMMON = 'team.Global.Common';

interface LoaderProps
  extends Omit<GridLoadingOverlayProps, 'color'>,
    LoadingOverlayPropsOverrides {
  color?: string;
}

const Loader = (props: LoaderProps) => {
  return <LinearProgress color={'primary'} />;
};

function CustomToolbar({ pageSize }: { pageSize: number }) {
  const dispatch = useDispatch();
  const { t: tMCS } = useTranslation([], {
    keyPrefix: 'team.Modals.SalesOrdersSearch',
  });
  const { searchInput, searchBy, searchLoading, resp } = useSelector(
    (store) => store.orderSearchModalSlice,
  );
  const { team } = useSelector((store) => store.userSlice);

  const debouncedSearch = useCallback(
    debounce((searchQuery: string) => {
      const payload: OrderSearchInput = {
        query: searchQuery,
        searchBy,
        size: pageSize,
        page: 0,
      };
      dispatch(searchOrders(payload));
    }, 1500),
    [searchBy],
  );

  if (team === 'init' || team === 'loading') return null;

  return (
    <GridToolbarContainer>
      <SearchByDropdown />
      <TextField
        value={searchInput}
        onChange={(e) => {
          dispatch(setSearchLoading(true));
          dispatch(setSearchInput(e.currentTarget.value));
          debouncedSearch(e.currentTarget.value);
        }}
        id="filled-basic"
        label={tMCS('Search for sales orders...')}
        variant="outlined"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <React.Fragment>
              {searchLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
            </React.Fragment>
          ),
        }}
      />
    </GridToolbarContainer>
  );
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuColumnsOrder: null,
        columnMenuFilterOrder: null,
      }}
    />
  );
}

interface TableProps {
  multiSelect: boolean;
}

export default function Table({ multiSelect }: TableProps) {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const { resp, searchLoading, searchInput, searchBy } = useSelector(
    (store) => store.orderSearchModalSlice,
  );

  // 2) Handle row selection
  const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
    // If single selection is desired but newSelectionModel has >1 items,
    // keep only the last item.
    let finalSelectionModel = newSelectionModel;
    if (!multiSelect && newSelectionModel.length > 1) {
      const lastId = newSelectionModel[newSelectionModel.length - 1];
      if (lastId !== undefined) {
        finalSelectionModel = [lastId];
      }
    }

    // Filter the rows to get the full objects of the selected orders
    const rows = resp ? resp.results : [];
    const selected = rows.filter((row) =>
      finalSelectionModel.includes(row.OrderId),
    );
    dispatch(setSelectedOrders(selected));
  };

  const baseColumns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Order Code',
        type: 'string',
        flex: 1,
        valueGetter: (value, row) => {
          return row.code;
        },
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Order Code"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'customerDeposit',
        headerName: 'Customer Deposit',
        flex: 1,
        valueGetter: (value, row) => {
          return row.customerDeposit;
        },
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Deposit"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.value}</span>
        ),
      },
      {
        field: 'rate',
        headerName: 'Rate',
        flex: 1,
        valueGetter: (value, row) => {
          return row.rate;
        },
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Rate"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{`${params.value * 100}%`}</span>
        ),
      },
    ],
    [],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: multiSelect
      ? GRID_CHECKBOX_COLUMN_ORDER
      : EMPTY_LEADING_GRID_COLUMNS,
  });

  const columns = React.useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <DataGridPro
      apiRef={apiRef}
      columnHeaderHeight={56}
      sx={{
        border: 'none',
        padding: '1rem',
        '.MuiDataGrid-row:nth-of-type(even):not(.Mui-selected)': {
          backgroundColor: '#ECECEC',
        },
        '.MuiDataGrid-row.Mui-selected': {
          backgroundColor: (theme) =>
            hexToRGBA(theme.palette.primary.main, 0.19),
        },
        '.MuiDataGrid-row.Mui-selected:hover': {
          backgroundColor: (theme) =>
            hexToRGBA(theme.palette.primary.main, 0.19),
        },
        '.MuiDataGrid-scrollbar--horizontal': { display: 'none' },
        '.MuiDataGrid-columnHeaderTitle': {
          fontWeight: 'bold',
          whiteSpace: 'normal',
          lineHeight: 1.2,
        },
        '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },
      }}
      rows={
        resp
          ? resp.results.map((order) => ({
              ...order,
              id: order.OrderId,
            }))
          : []
      }
      onRowSelectionModelChange={handleSelectionChange}
      checkboxSelection={multiSelect} // show checkboxes only if multiSelect=true
      disableRowSelectionOnClick={multiSelect ? true : false}
      loading={searchLoading}
      columns={columns}
      columnVisibilityModel={persistedPrefs.columnVisibilityModel}
      onColumnVisibilityModelChange={
        persistedPrefs.onColumnVisibilityModelChange
      }
      onColumnWidthChange={persistedPrefs.onColumnWidthChange}
      onColumnOrderChange={persistedPrefs.onColumnOrderChange}
      pagination
      paginationMode="server"
      paginationModel={{
        page: resp?.page || 0,
        pageSize: resp?.size || 0,
      }}
      rowCount={resp?.total || 0}
      pageSizeOptions={[initialPageSize, 25, 50, 100]}
      onPaginationModelChange={(newModel) => {
        const payload: OrderSearchInput = {
          query: searchInput,
          searchBy,
          page: newModel.page,
          size: newModel.pageSize,
        };
        dispatch(searchOrders(payload));
      }}
      pinnedColumns={
        multiSelect ? { left: [GRID_CHECKBOX_SELECTION_FIELD] } : undefined
      }
      disableColumnMenu
      autoHeight={false}
      slots={{
        loadingOverlay: Loader,
        toolbar: () => (
          <CustomToolbar pageSize={resp?.size || initialPageSize} />
        ),
        columnMenu: CustomColumnMenu,
      }}
    />
  );
}
